import {useState, useEffect, useCallback} from 'react'
import {SchedulePayload, ScheduleResponse} from '../../../state/rest'
import {Schedule, PeriodType} from './Scheduler'
import {ScheduleFrequency} from './TaskScheduling'

const getSavedFrequency = (schedule?: ScheduleResponse): ScheduleFrequency | null => {
  if (!schedule) return null
  if (schedule.period.days !== undefined) return 'week'
  if (schedule.period.months !== undefined) return 'month'
  return 'year'
}

export function useScheduler(saved?: ScheduleResponse) {
  const [schedulingEnabled, setSchedulingEnabled] = useState(!!saved)
  const [selectedFrequency, setSelectedFrequency] = useState<ScheduleFrequency | null>(getSavedFrequency(saved))
  const [scheduledPayload, setSchedulerPayload] = useState<SchedulePayload | null>(null)
  const calculateSchedule = useCallback(
    (s: Schedule) => {
      const payload: SchedulePayload = {
        id: saved ? saved.id : undefined,
        dates: `[${s.startDate},)`,
        period: `${s.frequency} ${s.periodType}`,
        time: s.startTime,
        weekdays: s.periodType === PeriodType.WEEK && s.weekDays ? s.weekDays : undefined,
        monthdays: s.periodType === PeriodType.MONTH && s.days ? s.days : undefined,
        yeardays: s.periodType === PeriodType.YEAR && s.dates ? s.dates : undefined
      }
      return payload
    },
    [saved]
  )
  function reverseSchedule(s?: ScheduleResponse): Schedule | undefined {
    if (!s) {
      return undefined
    }
    const periodType = Object.keys(s.period)[0]
    let payload = {
      startDate: s.dates.substring(1, 11),
      startTime: s.time
    }

    if (periodType === 'days') {
      return {
        ...payload,
        frequency: s.period.days / 7,
        periodType: PeriodType.WEEK,
        weekDays: s.weekdays ? s.weekdays.map(w => parseInt(w)) : undefined
      }
    }
    if (periodType === 'months') {
      return {
        ...payload,
        frequency: s.period.months!,
        periodType: PeriodType.MONTH,
        days: s.monthdays ? s.monthdays.map(m => parseInt(m)) : undefined
      }
    } else {
      return {
        ...payload,
        frequency: s.period.years!,
        periodType: PeriodType.YEAR,
        dates: s.yeardays
      }
    }
  }
  useEffect(() => {
    setSchedulingEnabled(!!saved)
    setSelectedFrequency(getSavedFrequency(saved))
    if (saved) {
      setSchedulerPayload(calculateSchedule(reverseSchedule(saved)!))
    } else {
      setSchedulerPayload(null)
    }
  }, [saved, calculateSchedule])
  function toggleShow() {
    setSchedulingEnabled(!schedulingEnabled)
    setSelectedFrequency(null)
    setSchedulerPayload(!schedulingEnabled && saved ? calculateSchedule(reverseSchedule(saved)!) : null)
  }
  const handleScheduler = useCallback(
    (s: Schedule) => {
      setSchedulerPayload(calculateSchedule(s))
    },
    [calculateSchedule]
  )

  const scheduleIncomplete = schedulingEnabled && (!saved || selectedFrequency !== getSavedFrequency(saved))

  return {
    toggleShow,
    handleScheduler,
    reverseSchedule,
    scheduledPayload,
    schedulingEnabled,
    selectedFrequency,
    setSelectedFrequency,
    scheduleIncomplete
  }
}
